/**
 * Music ducking under SFX: the music dips −6 dB for 350 ms, then recovers.
 *
 *   gain
 *   1.0 ─┐                           ╭──────
 *        │╲  attack (≤ 20 ms, linear) ╱ release (220 ms, exponential)
 *   0.5  │ ╰────────── hold ─────────╯
 *        t   ←──── 350 ms from the (latest) trigger ────→
 *
 * A retrigger during a duck starts from the exact current gain (see
 * automation.ts), shortens the attack proportionally if already dipped, and
 * extends the hold — so overlapping SFX never pump or click.
 *
 * Pure and DOM-free (unit-tested in node).
 */
import { ParamTrack, type AutomationEvent } from './automation.ts';
import { clamp01, dbToGain } from './mix.ts';

export interface DuckConfig {
  depthDb: number;
  holdMs: number;
  attackMs: number;
  releaseMs: number;
}

export const DUCK: Readonly<DuckConfig> = {
  depthDb: -6,
  holdMs: 350,
  attackMs: 20,
  releaseMs: 220,
};

/** Linear gain at full duck depth (−6 dB ≈ 0.501). */
export function duckDepthGain(cfg: Readonly<DuckConfig> = DUCK): number {
  return dbToGain(cfg.depthDb);
}

/**
 * Plan a duck triggered at `now` on the ducking gain's track. Returns the
 * automation to write (after cancelling from `now`).
 */
export function planDuck(track: ParamTrack, now: number, cfg: Readonly<DuckConfig> = DUCK): AutomationEvent[] {
  const depth = duckDepthGain(cfg);
  const current = track.valueAt(now);
  // Already partly ducked → only the remaining distance needs an attack.
  const attackS = (cfg.attackMs / 1000) * clamp01((current - depth) / (1 - depth));
  const holdEnd = now + cfg.holdMs / 1000;
  return track.replace([
    { type: 'set', time: now, value: current },
    { type: 'linear', time: now + attackS, value: depth },
    { type: 'set', time: holdEnd, value: depth },
    { type: 'exp', time: holdEnd + cfg.releaseMs / 1000, value: 1 },
  ]);
}
